'use client';

import React, { useRef, useEffect, useState } from 'react';
import Image from 'next/image';
import { Section } from '@/components/ui/Section';
import styles from './QRShowcase.module.css';

export interface QRShowcaseProps {
  title: string;
  subtitle: string;
  altTexts?: {
    annemax?: string;
    laplace?: string;
    tastoe?: string;
    general?: string;
  };
}

// QR Mockup configuration with brand-specific glow colors
const QR_MOCKUP_CONFIG = [
  {
    id: 'annemax',
    src: '/images/mockups/qr-mockup-anne-max.webp',
    color: '#36868a', // Anne&Max brand teal
  },
  {
    id: 'tastoe',
    src: '/images/mockups/qr-mockup-tastoe.webp',
    color: '#d92f89', // Tastoe brand pink
  },
  {
    id: 'laplace',
    src: '/images/mockups/qr-mockup-la-place.webp',
    color: '#658d1b', // La Place brand green
  },
  {
    id: 'general',
    src: '/images/mockups/qr-mockup.webp',
    color: '#f47840', // Jamezz brand orange
  },
] as const;

/**
 * QRShowcase Component
 * Displays 4 QR ordering mockups with floating animation effect
 * On mobile: auto-playing slider with touch support
 * Reuses KioskShowcase styles but with QR mockup images
 */
export function QRShowcase({ title, subtitle, altTexts }: QRShowcaseProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  // Auto-play slider on mobile (check happens in useEffect to avoid SSR mismatch)
  useEffect(() => {
    const isMobileDevice = window.innerWidth <= 768;
    if (!isMobileDevice) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 4);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Touch handlers for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const diff = touchStartX.current - touchEndX.current;
    const threshold = 50; // Minimum swipe distance

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swiped left - next slide
        setCurrentSlide((prev) => (prev + 1) % 4);
      } else {
        // Swiped right - previous slide
        setCurrentSlide((prev) => (prev - 1 + 4) % 4);
      }
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  // Build mockups array with translated alt texts
  const qrMockups = QR_MOCKUP_CONFIG.map((mockup) => ({
    ...mockup,
    alt: altTexts?.[mockup.id as keyof typeof altTexts] || 
         `Jamezz QR ordering - ${mockup.id}`,
  }));

  return (
    <Section background="primary" paddingY="none">
      <div className={styles.showcase} ref={sectionRef}>
      <div className={styles.background}>
      {/* BACKUP - Gradient orbs (Removed 2026-02-11 for CLS fix)
           Uncomment to restore if needed
      <div className={styles.gradientOrb1} />
      <div className={styles.gradientOrb2} />
      */}
        <div className={styles.grid} />
      </div>

      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        <div 
          className={styles.kiosksGrid}
          ref={sliderRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          data-current-slide={currentSlide}
        >
          {qrMockups.map((mockup, index) => (
            <div
              key={mockup.id}
              className={styles.kiosk}
            >
              <div className={styles.kioskInner}>
                <div
                  className={styles.kioskGlow}
                  style={{
                    background: `radial-gradient(circle at center, ${mockup.color}40 0%, transparent 70%)`,
                  }}
                />
                <div className={styles.imageWrapper}>
                  <Image
                    src={mockup.src}
                    alt={mockup.alt}
                    width={400}
                    height={800}
                    className={styles.kioskImage}
                    priority={index === 0}
                    quality={85}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation dots - CSS hides on desktop, visible immediately on mobile */}
          <div className={styles.sliderDots}>
            {qrMockups.map((mockup, index) => (
              <button
                key={index}
                className={`${styles.dot} ${index === currentSlide ? styles.dotActive : ''}`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                style={{
                  background: index === currentSlide ? mockup.color : `${mockup.color}40`,
                  borderColor: index === currentSlide ? mockup.color : `${mockup.color}60`,
                  boxShadow: index === currentSlide ? `0 0 16px ${mockup.color}99` : 'none',
                }}
              />
            ))}
          </div>
      </div>
      </div>
    </Section>
  );
}

